import { Controller } from "@hotwired/stimulus";

/**
 * The recap beside the one-page evaluation form (30/10/2026): the date, the
 * times and the scale follow what is typed, without reloading the page.
 * The class, the course and the period already reload it (form-refresh).
 *
 * <form data-controller="evaluation-summary"
 *       data-action="change->evaluation-summary#update input->evaluation-summary#update"
 *       data-evaluation-summary-out-of-value="sur %{scale}"
 *       data-evaluation-summary-competences-value='{"zero":"…","one":"…","other":"%{count} …"}'>
 *   <dd data-evaluation-summary-target="when">…</dd>
 *   <dd data-evaluation-summary-target="scale">…</dd>
 */
export default class extends Controller {
  static targets = ["when", "scale"];
  static values = { outOf: String, competences: Object, empty: { type: String, default: "—" } };

  update() {
    if (this.hasWhenTarget) this.whenTarget.textContent = this.when() || this.emptyValue;
    const scale = this.scale();
    if (this.hasScaleTarget && scale !== null) this.scaleTarget.textContent = scale || this.emptyValue;
  }

  when() {
    const date = this.field("evaluation[date]");
    const times = [this.field("evaluation[start_time]"), this.field("evaluation[end_time]")].filter(Boolean).join("-");
    return [this.longDate(date), times].filter(Boolean).join(", ");
  }

  // Secondary: « sur N »; primary: the competences ticked; nursery: null, the recap is kept.
  scale() {
    const ticked = this.element.querySelectorAll('input[type="checkbox"][name^="evaluation[competences]"][name$="[selected]"]');
    if (ticked.length > 0) {
      const count = [...ticked].filter((box) => box.checked).length;
      const key = count === 0 ? "zero" : count === 1 ? "one" : "other";
      return (this.competencesValue[key] || "").replace("%{count}", count);
    }
    const scale = this.element.querySelector('input[type="number"][name="evaluation[scale]"]');
    return scale && this.hasOutOfValue ? this.outOfValue.replace("%{scale}", scale.value || "") : null;
  }

  field(name) {
    return this.element.querySelector(`[name="${name}"]`)?.value?.trim() || "";
  }

  longDate(iso) {
    const match = /^(\d{4})-(\d{2})-(\d{2})$/.exec(iso);
    if (!match) return iso;
    const date = new Date(Number(match[1]), Number(match[2]) - 1, Number(match[3]));
    return new Intl.DateTimeFormat(document.documentElement.lang || "fr", {
      day: "numeric", month: "long", year: "numeric",
    }).format(date);
  }
}
